import React, { useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AlternativesList from "@/components/AlternativesList";
import CategoryFilter from "@/components/CategoryFilter";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";
import { Globe, Mail, MessageSquare, File, Lock, Image, Smartphone, Search, Monitor } from "lucide-react";

const Alternatives = () => {
  const { t } = useTranslation("alternativ");
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = useMemo(
    () => [
      { id: "communication", name: t("categories.communication"), icon: <Mail className="h-4 w-4" /> },
      { id: "productivity", name: t("categories.productivity"), icon: <File className="h-4 w-4" /> },
      { id: "browsing", name: t("categories.browsing"), icon: <Globe className="h-4 w-4" /> },
      { id: "search", name: t("categories.search"), icon: <Search className="h-4 w-4" /> },
      { id: "messaging", name: t("categories.messaging"), icon: <MessageSquare className="h-4 w-4" /> },
      { id: "os", name: t("categories.os"), icon: <Monitor className="h-4 w-4" /> },
      { id: "security", name: t("categories.security"), icon: <Lock className="h-4 w-4" /> },
      { id: "social", name: t("categories.social"), icon: <Smartphone className="h-4 w-4" /> },
      { id: "media", name: t("categories.media"), icon: <Image className="h-4 w-4" /> },
    ],
    [t],
  );

  // NOTE: Du använder fältet "eu" som "alternativ" även när alternativet inte är EU.
  // Säkerställ att UI-texten inte kallar kolumnen för "EU-alternativ" om du vill slippa gnäll.

  const alternativesData = useMemo(
    () => [
      {
        id: "browsing",
        title: t("sections.browsing.title"),
        description: t("sections.browsing.description"),
        alternatives: [
          {
            us: "Google Chrome, Microsoft Edge, Safari",
            eu: "Firefox",
            description: "Webb, Desktop, Mobil. Mozilla (US) med stark EU-närvaro. Gratis och öppen källkod.",
            country: "Globalt (Mozilla)",
            platform: "Webb, Desktop, Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://www.mozilla.org/firefox/",
            descriptionKey: "firefox",
          },
          {
            us: "Google Chrome, Microsoft Edge, Safari",
            eu: "Firefox Focus",
            description: "Mobil. Fokuserad på integritet med automatisk historikradering.",
            country: "Globalt (Mozilla)",
            platform: "Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://www.mozilla.org/firefox/browsers/mobile/focus/",
            descriptionKey: "firefox_focus",
          },
          {
            us: "Google Chrome, Microsoft Edge, Safari",
            eu: "Cromite",
            description: "Android. Chromium-baserad webbläsare med integritetsskydd.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://github.com/uazo/cromite",
            descriptionKey: "cromite",
          },
          {
            us: "Google Chrome, Microsoft Edge, Safari",
            eu: "LibreWolf",
            description:
              "Desktop. Anpassad version av Firefox med ytterligare integritetsskydd och säkerhetsinställningar.",
            country: "FOSS-community",
            platform: "Desktop",
            openSource: true,
            selfHosted: false,
            url: "https://librewolf.net/",
            descriptionKey: "librewolf",
          },
          {
            us: "Google Chrome, Microsoft Edge, Safari",
            eu: "Brave",
            description:
              "Webb, Desktop, Mobil. Chromium-baserad med inbyggt spårnings-/annonsblock. Populär men USA-baserad.",
            country: "USA",
            platform: "Webb, Desktop, Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://brave.com/",
            descriptionKey: "brave",
          },
        ],
      },
      {
        id: "search",
        title: t("sections.search.title"),
        description: t("sections.search.description"),
        alternatives: [
          {
            us: "Google, Bing, Yahoo",
            eu: "Qwant",
            description:
              "Webb. Europeisk sökmotor med integritetsfokus. (Obs: delar av infrastrukturen bygger ofta på tredjepartsindex.)",
            country: "Frankrike",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://www.qwant.com/",
            descriptionKey: "qwant",
          },
          {
            us: "Google, Bing, Yahoo",
            eu: "Mojeek",
            description: "Webb. Sökmotor med eget index som inte spårar användare.",
            country: "UK",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://www.mojeek.com/",
            descriptionKey: "mojeek",
          },
          {
            us: "Google, Bing, Yahoo",
            eu: "SearXNG",
            description: "Webb. Metasökmotor som kan självhostas för maximal integritet.",
            country: "FOSS-community",
            platform: "Webb (självhostbar)",
            openSource: true,
            selfHosted: true,
            url: "https://github.com/searxng/searxng",
            descriptionKey: "searxng",
          },
        ],
      },
      {
        id: "os",
        title: t("sections.os.title"),
        description: t("sections.os.description"),
        alternatives: [
          {
            us: "Windows, macOS",
            eu: "Linux Mint",
            description:
              "Desktop. Användarvänlig Linux-distribution. (Ubuntu/Fedora är också bra alternativ, men Mint är oftast enklast för nybörjare.)",
            country: "FOSS (globalt)",
            platform: "Desktop",
            openSource: true,
            selfHosted: false, // OS är inte "self-hosted" i vanlig mening
            url: "https://linuxmint.com/",
            descriptionKey: "linux_mint",
          },
          {
            us: "Android",
            eu: "/e/OS",
            description: "Mobil. Android-baserat operativsystem med fokus på integritet och utan Google som standard.",
            country: "Frankrike",
            platform: "Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://e.foundation/",
            descriptionKey: "eos",
          },
          {
            us: "Android",
            eu: "GrapheneOS",
            description: "Mobil. Säkerhetsfokuserad Android-distribution. (Främst för Pixel-enheter.)",
            country: "Kanada",
            platform: "Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://grapheneos.org/",
            descriptionKey: "graphene",
          },
          {
            us: "Android",
            eu: "postmarketOS",
            description: "Mobil. Linux-baserat mobil-OS, ofta för äldre/utvalda enheter (mer avancerat).",
            country: "FOSS-community",
            platform: "Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://postmarketos.org/",
            descriptionKey: "postmarket",
          },
        ],
      },
      {
        id: "communication",
        title: t("sections.communication.title"),
        description: t("sections.communication.description"),
        alternatives: [
          {
            us: "Gmail, Outlook, Yahoo Mail, iCloud Mail",
            eu: "Proton Mail",
            description:
              "Webb, Mobil. Krypteringsfokus. Klienter är open source; tjänsten är inte självhostbar (och e-post är inte E2E mot alla mottagare per automatik).",
            country: "Schweiz",
            platform: "Webb, Mobil",
            openSource: false, // undvik "men backend då?!"-gnäll
            selfHosted: false,
            url: "https://proton.me/mail",
            descriptionKey: "protonmail",
          },
          {
            us: "Gmail, Outlook, Yahoo Mail, iCloud Mail",
            eu: "Tuta",
            description: "Webb, Mobil. Krypterad e-post med fokus på säkerhet och integritet.",
            country: "Tyskland",
            platform: "Webb, Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://tuta.com/",
            descriptionKey: "tuta",
          },
          {
            us: "Gmail, Outlook, Yahoo Mail, iCloud Mail",
            eu: "Mailbox.org",
            description: "Webb. Heltäckande e-postlösning för både privata användare och företag.",
            country: "Tyskland",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://mailbox.org/",
            descriptionKey: "mailbox",
          },
          {
            us: "Gmail, Outlook, Yahoo Mail, iCloud Mail",
            eu: "StartMail",
            description: "Webb. Betalande e-posttjänst med fokus på integritet.",
            country: "Nederländerna",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://www.startmail.com/",
            descriptionKey: "startmail",
          },
          {
            us: "Zoom, Google Meet, Microsoft Teams",
            eu: "Whereby",
            description: "Webb. Videomöten utan installation. Väldigt lätt att komma igång med (ej FOSS).",
            country: "Norge",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://whereby.com/",
            descriptionKey: "whereby",
          },
          {
            us: "Zoom, Google Meet, Microsoft Teams",
            eu: "Jitsi Meet",
            description: "Webb, Mobil. Öppen källkod. Kan köras som tjänst eller självhostas.",
            country: "FOSS (globalt)",
            platform: "Webb, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://jitsi.org/",
            descriptionKey: "jitsi",
          },
          {
            us: "Zoom, Google Meet, Microsoft Teams",
            eu: "BigBlueButton",
            description: "Webb. Komplett videoplattform (lite tyngre). Bra för utbildning/organisationer.",
            country: "Kanada",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://bigbluebutton.org/",
            descriptionKey: "bigbluebutton",
          },
        ],
      },
      {
        id: "messaging",
        title: t("sections.messaging.title"),
        description: t("sections.messaging.description"),
        alternatives: [
          {
            us: "WhatsApp, Facebook Messenger, iMessage",
            eu: "Signal",
            description: "Mobil, Desktop. Krypterad meddelandeapp med fokus på säkerhet. Lägst friktion.",
            country: "USA (ideell drift)",
            platform: "Mobil, Desktop",
            openSource: true,
            selfHosted: false,
            url: "https://signal.org",
            descriptionKey: "signal",
          },
          {
            us: "WhatsApp, Facebook Messenger, iMessage",
            eu: "Element (Matrix)",
            description: "Webb, Mobil. Decentraliserad plattform baserad på Matrix-protokollet. Kan självhostas.",
            country: "UK / decentraliserad",
            platform: "Webb, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://element.io",
            descriptionKey: "element",
          },
          {
            us: "WhatsApp, Facebook Messenger, iMessage",
            eu: "Session",
            description: "Mobil, Desktop. Krypterad meddelandeapp utan krav på telefonnummer eller e-post.",
            country: "Australien",
            platform: "Mobil, Desktop",
            openSource: true,
            selfHosted: false,
            url: "https://getsession.org/",
            descriptionKey: "session",
          },
          {
            us: "WhatsApp, Facebook Messenger, iMessage",
            eu: "Briar",
            description: "Android. Peer-to-peer och kan fungera även utan internet (mer nischad).",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://briarproject.org/",
            descriptionKey: "briar",
          },
        ],
      },
      {
        id: "productivity",
        title: t("sections.productivity.title"),
        description: t("sections.productivity.description"),
        alternatives: [
          {
            us: "Google Drive, OneDrive, iCloud",
            eu: "Nextcloud",
            description:
              "Webb, Desktop, Mobil. Självhostad molnplattform med filsynk, delning, kalender, kontakter och mer.",
            country: "Tyskland",
            platform: "Webb, Desktop, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://nextcloud.com",
            descriptionKey: "nextcloud",
          },
          {
            us: "Google Drive, OneDrive, iCloud",
            eu: "Proton Drive",
            description:
              "Webb, Mobil. Krypterad molnlagring. Klienter kan vara open source; tjänsten är inte självhostbar.",
            country: "Schweiz",
            platform: "Webb, Mobil",
            openSource: false,
            selfHosted: false,
            url: "https://proton.me/drive",
            descriptionKey: "protondrive",
          },
          {
            us: "Google Drive, OneDrive, iCloud",
            eu: "Internxt",
            description: "Webb. Molnlagring med end-to-end kryptering och fokus på integritet.",
            country: "Spanien",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://internxt.com/",
            descriptionKey: "internxt",
          },
          {
            us: "Microsoft Office / 365 / Google Docs",
            eu: "LibreOffice",
            description: "Desktop. Fullständigt kontorspaket med öppen källkod.",
            country: "FOSS (globalt)",
            platform: "Desktop",
            openSource: true,
            selfHosted: false,
            url: "https://www.libreoffice.org",
            descriptionKey: "libreoffice",
          },
          {
            us: "Microsoft Office / 365 / Google Docs",
            eu: "OnlyOffice",
            description: "Webb, Desktop. Komplett kontorssuite. Vanligt ihop med Nextcloud för samarbete.",
            country: "Lettland",
            platform: "Webb, Desktop",
            openSource: true,
            selfHosted: true,
            url: "https://www.onlyoffice.com/",
            descriptionKey: "onlyoffice",
          },
          {
            us: "Microsoft Office / 365 / Google Docs",
            eu: "CryptPad",
            description: "Webb. Zero-knowledge kollaborativ dokumenteditor. Kan självhostas.",
            country: "Frankrike",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://cryptpad.fr",
            descriptionKey: "cryptpad",
          },
          {
            us: "Google Calendar, Apple Calendar",
            eu: "Nextcloud Calendar",
            description: "Webb, Mobil. Kalenderkomponent för Nextcloud.",
            country: "Tyskland",
            platform: "Webb, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://apps.nextcloud.com/apps/calendar",
            descriptionKey: "nextcloudcalendar",
          },
          {
            us: "Google Calendar, Apple Calendar",
            eu: "Fossify Calendar",
            description:
              "Android. Enkel kalenderapp med öppen källkod. Community-driven fortsättning på “Simple”-sviten.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://www.fossify.org/apps/",
            descriptionKey: "fossifycalendar",
          },
          {
            us: "Google Calendar, Apple Calendar",
            eu: "Etar",
            description: "Android. Kalenderapp med öppen källkod.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://github.com/Etar-Group/Etar-Calendar",
            descriptionKey: "etar",
          },
          {
            us: "Slack, Microsoft Teams",
            eu: "Mattermost",
            description:
              "Webb, Desktop, Mobil. Teamchatt/samarbete med fokus på säkerhet och integration. Självhostbar.",
            country: "USA (OSS, självhostbar)",
            platform: "Webb, Desktop, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://mattermost.com/",
            descriptionKey: "mattermost",
          },
        ],
      },
      {
        id: "social",
        title: t("sections.social.title"),
        description: t("sections.social.description"),
        alternatives: [
          {
            us: "Twitter/X",
            eu: "Mastodon",
            description: "Webb, Mobil. Decentraliserat socialt nätverk (Fediverse) utan beteendespårande algoritmer.",
            country: "Decentraliserad (Fediverse)",
            platform: "Webb, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://joinmastodon.org",
            descriptionKey: "mastodon",
          },
          {
            us: "Instagram",
            eu: "Pixelfed",
            description: "Webb. Fotodelning i Fediverse utan algoritmisk sortering.",
            country: "Decentraliserad (Fediverse)",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://pixelfed.org",
            descriptionKey: "pixelfed",
          },
          {
            us: "Facebook",
            eu: "Friendica",
            description: "Webb. Fediverse-kompatibelt socialt nätverk med mer “Facebook-lik” struktur.",
            country: "Decentraliserad (Fediverse)",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://friendi.ca/",
            descriptionKey: "friendica",
          },
          {
            us: "Reddit",
            eu: "Lemmy",
            description: "Webb. Decentraliserad länkaggregator och diskussionsplattform.",
            country: "FOSS-community",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://join-lemmy.org/",
            descriptionKey: "lemmy",
          },
          {
            us: "Facebook Events",
            eu: "Mobilizon",
            description: "Webb. Plattform för att organisera och hitta evenemang utan datainsamling.",
            country: "Frankrike",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://mobilizon.org/",
            descriptionKey: "mobilizon",
          },
          {
            us: "LinkedIn",
            eu: "Nolto",
            description: "Webb. Öppet källkod-professionellt nätverk och alternativ till LinkedIn. Federerat (ActivityPub) så du kan nås från Mastodon. Kan självhostas.",
            country: "Sverige",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://nolto.social/",
            descriptionKey: "nolto",
          },
        ],
      },
      {
        id: "media",
        title: t("sections.media.title"),
        description: t("sections.media.description"),
        alternatives: [
          {
            us: "YouTube",
            eu: "PeerTube",
            description: "Webb. Decentraliserad videoplattform (Fediverse) utan beteendespårande.",
            country: "Decentraliserad (Fediverse)",
            platform: "Webb",
            openSource: true,
            selfHosted: true,
            url: "https://joinpeertube.org",
            descriptionKey: "peertube",
          },
          {
            us: "YouTube",
            eu: "NewPipe",
            description: "Android. Lätt YouTube-klient utan Google-inlogg och med färre spårningsytor.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://newpipe.net/",
            descriptionKey: "newpipe",
          },
          {
            us: "YouTube",
            eu: "Invidious",
            description: "Webb. Alternativt gränssnitt för YouTube. Kan självhostas.",
            country: "FOSS-community",
            platform: "Webb (självhostbar)",
            openSource: true,
            selfHosted: true,
            url: "https://github.com/iv-org/invidious",
            descriptionKey: "invidious",
          },
          {
            us: "Netflix, Spotify",
            eu: "Jellyfin",
            description: "Desktop, Webb, Mobil. Medieserver för att strömma ditt eget innehåll.",
            country: "FOSS-community",
            platform: "Desktop, Webb, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://jellyfin.org/",
            descriptionKey: "jellyfin",
          },
          {
            us: "Spotify",
            eu: "Funkwhale",
            description: "Webb, Mobil. Självhostad musikplattform med respekt för integritet.",
            country: "EU (FOSS)",
            platform: "Webb, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://funkwhale.audio",
            descriptionKey: "funkwhale",
          },
          {
            us: "Spotify, Bandcamp",
            eu: "Resonate",
            description: "Webb. Kooperativ musikplattform med fokus på rättvis ersättning till artister.",
            country: "EU (kooperativ)",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://resonate.is/",
            descriptionKey: "resonate",
          },
        ],
      },
      {
        id: "security",
        title: t("sections.security.title"),
        description: t("sections.security.description"),
        alternatives: [
          {
            us: "Google Play",
            eu: "F-Droid",
            description: "Android. Appbutik för fri och öppen källkod, med fokus på integritet.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://f-droid.org/",
            descriptionKey: "fdroid",
          },
          {
            us: "Google Play",
            eu: "Aurora Store",
            description: "Android. Anonym klient för Google Play med integritetsfokus.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://auroraoss.com/",
            descriptionKey: "aurorastore",
          },
          {
            us: "Google Play",
            eu: "Obtainium",
            description: "Android. Installera/uppdatera appar direkt från källor (t.ex. GitHub) med kontroll.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://github.com/ImranR98/Obtainium",
            descriptionKey: "obtainium",
          },
          {
            us: "Google Passwords, LastPass",
            eu: "Bitwarden",
            description: "Webb, Desktop, Mobil. Lösenordshanterare med kryptering. Kan självhostas.",
            country: "USA (OSS, självhostbar)",
            platform: "Webb, Desktop, Mobil",
            openSource: true,
            selfHosted: true,
            url: "https://bitwarden.com",
            descriptionKey: "bitwarden",
          },
          {
            us: "Google Passwords, LastPass",
            eu: "KeePassXC",
            description: "Desktop. Lokal lösenordshanterare med öppen källkod.",
            country: "FOSS-community",
            platform: "Desktop",
            openSource: true,
            selfHosted: false,
            url: "https://keepassxc.org/",
            descriptionKey: "keepassxc",
          },
          {
            us: "Google Passwords, LastPass",
            eu: "KeePassDX",
            description: "Android. Lösenordshanterare baserad på KeePass-formatet.",
            country: "FOSS-community",
            platform: "Android",
            openSource: true,
            selfHosted: false,
            url: "https://www.keepassdx.com/",
            descriptionKey: "keepassdx",
          },
          {
            us: "Google Maps, Apple Maps",
            eu: "Organic Maps",
            description:
              "Mobil. Offline-kartor utan spårning (OpenStreetMap-baserad). (Obs: det finns även community-forken CoMaps.)",
            country: "FOSS-community",
            platform: "Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://organicmaps.app/",
            descriptionKey: "organicmaps",
          },
          {
            us: "Google Maps, Apple Maps",
            eu: "OsmAnd",
            description: "Mobil. Detaljerade offlinekartor baserade på OpenStreetMap.",
            country: "FOSS-community",
            platform: "Mobil",
            openSource: true,
            selfHosted: false,
            url: "https://osmand.net/",
            descriptionKey: "osmand",
          },
          {
            us: "Google Maps, Apple Maps",
            eu: "HERE WeGo",
            description: "Mobil, Webb. Kartapp med navigering. (Ej open source.)",
            country: "Tyskland (ursprung: Nokia)",
            platform: "Mobil, Webb",
            openSource: false,
            selfHosted: false,
            url: "https://wego.here.com/",
            descriptionKey: "herewego",
          },
          {
            us: "Webbläsare utan spårningsskydd",
            eu: "uBlock Origin",
            description: "Webb. Tillägg som blockerar annonser/spårare effektivt.",
            country: "FOSS-community",
            platform: "Webb (tillägg)",
            openSource: true,
            selfHosted: false,
            url: "https://ublockorigin.com/",
            descriptionKey: "ublockorigin",
          },
          {
            us: "DNS utan filtrering",
            eu: "Pi-hole",
            description: "Nätverk. DNS-baserad blockering av annonser/spårare för hela hemnätverket. Självhostas.",
            country: "FOSS-community",
            platform: "Server / Hemnät",
            openSource: true,
            selfHosted: true,
            url: "https://pi-hole.net/",
            descriptionKey: "pihole",
          },
          {
            us: "BankID",
            eu: "Freja eID+",
            description: "Mobil. Svensk e-legitimation som alternativ till BankID.",
            country: "Sverige",
            platform: "Mobil",
            openSource: false,
            selfHosted: false,
            url: "https://frejaeid.com/",
            descriptionKey: "frejaeid",
          },
          {
            us: "Swish",
            eu: "SEPA Instant",
            description:
              "Banköverföring. Europeisk standard för omedelbara banköverföringar (infrastruktur, inte en “app”).",
            country: "EU-infrastruktur",
            platform: "Bank / Infrastruktur",
            openSource: false,
            selfHosted: false,
            url: "https://www.europeanpaymentscouncil.eu/what-we-do/sepa-instant-credit-transfer",
            descriptionKey: "sepa",
          },
          {
            us: "Swish",
            eu: "Trustly/Zimpler",
            description: "Webb. Betallösningar för direktöverföring från bankkonto (ej P2P-app som Swish).",
            country: "Sverige",
            platform: "Webb",
            openSource: false,
            selfHosted: false,
            url: "https://www.trustly.net/",
            descriptionKey: "trustly",
          },
        ],
      },
    ],
    [t],
  );

  const filteredAlternatives = useMemo(() => {
    if (activeCategory === "all") {
      return alternativesData;
    }
    return alternativesData.filter((category) => category.id === activeCategory);
  }, [activeCategory, alternativesData]);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title={`${t("page.title")} - Frikopplad`} description={t("page.description1")} url="/alternativ" />
      <Navbar />
      <main className="flex-grow">
        <div className="container-custom py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-frikopplad-navy mb-8">{t("page.title")}</h1>
          <div className="mb-10">
            <p className="text-lg text-gray-700 mb-4">{t("page.description1")}</p>
            <p className="text-lg text-gray-700 mb-8">{t("page.description2")}</p>

            {/* Category Filter */}
            <div className="mb-12">
              <CategoryFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
              />
            </div>
          </div>

          {filteredAlternatives.map((section) => (
            <div key={section.id} className="mb-16">
              <AlternativesList
                title={section.title}
                description={section.description}
                alternatives={section.alternatives}
                categoryId={section.id}
              />
            </div>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Alternatives;
